import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Heading, Modal } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyRestrictionsWebApiHandler_get,
  useRentalManagerUserPropertySummaryWebApiHandler_leases,
  useRentalManagerUserPropertySummaryWebApiHandler_payments,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import PropertiesActions from '../../../actions/PropertiesActions';
import { useGetApplicationsSummary } from '../../../hooks/queries/summary';
import {
  syncOnDataChangePropertyDetailsOverview,
  useGetPropertyDetailsOverview,
} from '../../../hooks/queries/useGetPropertyDetailsOverview';
import { RoutingSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import DispatchConstants from '../../../constants/DispatchConstants';
import PropertyConstants from '../../../constants/PropertyConstants';
import TimeConstants from '../../../constants/TimeConstants';

import EditPropertyType from '../../../components/EditPropertyType';
import ModalFooter from '../../../components/Modal/ModalFooter';

import type { CorrectPropertyTypes } from '../../../components/EditPropertyType';
import type { ChangeEvent } from 'react';

const { PROPERTY_TYPES_BY_CODE, PROPERTY_CODES_BY_TYPE } = PropertyConstants;

type Props = {
  setShowEditPropertyTypeModal: (value: boolean) => void;
};

const EditPropertyTypeModal = ({ setShowEditPropertyTypeModal }: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const currentProperty = useSelector((state) => state.properties.currentProperty);
  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));

  const { refetch: refetchRestrictions } = useRentalManagerUserPropertyRestrictionsWebApiHandler_get(
    { listingAlias },
    { enabled: Boolean(listingAlias), staleTime: TimeConstants.SECONDS_10 },
  );
  const { refetch: refetchApplicationsSummary } = useGetApplicationsSummary(listingAlias);
  const { refetch: refetchPaymentsSummary } = useRentalManagerUserPropertySummaryWebApiHandler_payments(
    { listingAlias },
    { enabled: Boolean(listingAlias), staleTime: TimeConstants.SECONDS_10 },
  );
  const { refetch: refetchGetLeasesSummary } = useRentalManagerUserPropertySummaryWebApiHandler_leases(
    { listingAlias },
    { enabled: Boolean(listingAlias), staleTime: TimeConstants.SECONDS_10, retry: false },
  );
  const { data: propertyDetailsOverviewData, isLoading } = useGetPropertyDetailsOverview(listingAlias);

  const [hasUpdatePropertyTypeError, setHasUpdatePropertyTypeError] = useState(false);
  const [correctPropertyType, setCorrectPropertyType] = useState<CorrectPropertyTypes | undefined>();

  useEffect(() => {
    if (propertyDetailsOverviewData) {
      syncOnDataChangePropertyDetailsOverview({ data: propertyDetailsOverviewData, currentProperty, dispatch });
    }

    // @ts-expect-error propertyDetailsOverviewData?.propertyType?.code
    if (propertyDetailsOverviewData?.propertyType?.code) {
      setCorrectPropertyType(
        // @ts-expect-error propertyDetailsOverviewData?.propertyType?.code
        PROPERTY_TYPES_BY_CODE[propertyDetailsOverviewData.propertyType.code as number] as CorrectPropertyTypes,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyDetailsOverviewData]);

  if (isLoading) {
    return null;
  }

  const {
    // @ts-expect-error propertyDetailsOverviewData?.propertyType?.code
    propertyType: { code },
  } = propertyDetailsOverviewData;

  const invalidateAndRefetchQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['getPropertyDetailsOverview', listingAlias] });
    queryClient.invalidateQueries({ queryKey: ['getRestrictions', listingAlias] });
    queryClient.invalidateQueries({ queryKey: ['useGetLeasesSummary', listingAlias] });

    refetchRestrictions();
    refetchApplicationsSummary();
    refetchGetLeasesSummary();
    refetchPaymentsSummary();
  };

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setHasUpdatePropertyTypeError(false);
    setCorrectPropertyType(e.target.value as CorrectPropertyTypes);
  };

  const handleEditPropertySubmit = () => {
    const newPropertyTypeCode = PROPERTY_CODES_BY_TYPE[correctPropertyType as CorrectPropertyTypes];
    dispatch(
      PropertiesActions.updatePropertyType({
        propertyId: listingAlias,
        propertyTypeCode: newPropertyTypeCode,
      }),
    )
      // @ts-expect-error .then() callback
      .then(() => {
        dispatch({
          type: DispatchConstants.UPDATE_PROPERTY_OVERVIEW,
          payload: {
            propertyType: {
              code: newPropertyTypeCode,
              display: correctPropertyType,
            },
          },
        });
        dispatch(PropertiesActions.updateCurrentProperty({ propertyType: correctPropertyType }));
        setShowEditPropertyTypeModal(false);

        return;
      })
      .catch(() => setHasUpdatePropertyTypeError(true));
  };

  return (
    <Modal
      header={
        <Heading level={2} textStyle="heading-xs">
          Edit property type
        </Heading>
      }
      body={
        <>
          {/* eslint-disable-next-line constellation/heading-order */}
          <Heading level={4}>Select the correct property type</Heading>

          <EditPropertyType
            onSelectChange={handleSelectChange}
            correctPropertyType={correctPropertyType as CorrectPropertyTypes}
            onConvertComplete={() => {
              invalidateAndRefetchQueries();
              setShowEditPropertyTypeModal(false);
            }}
          />
        </>
      }
      footer={
        <ModalFooter
          onSubmit={handleEditPropertySubmit}
          submitText="Save"
          onCancel={() => setShowEditPropertyTypeModal(false)}
          hasError={hasUpdatePropertyTypeError}
          isSubmitDisabled={correctPropertyType === PROPERTY_TYPES_BY_CODE[code]}
        />
      }
      open={true}
      onOpenChange={() => setShowEditPropertyTypeModal(false)}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default EditPropertyTypeModal;
